import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, tokenStore, unwrap } from "@/api/client";
import { ApiError, toApiError } from "@/api/errors";
import type { components } from "@/api/schema";
import type { MediaItem, ProductStatus, ProductWrite, VendorProduct } from "@/api/types";
import { env } from "@/lib/env";

type S = components["schemas"];

export const vendorKeys = {
  store: ["vendor", "store"] as const,
  dashboard: ["vendor", "dashboard"] as const,
  policy: ["vendor", "policy"] as const,
  products: (q: object) => ["vendor", "products", q] as const,
  product: (id: string) => ["vendor", "product", id] as const,
  subscription: ["vendor", "subscription"] as const,
  invoices: ["vendor", "invoices"] as const,
  kyc: ["vendor", "kyc"] as const,
  banks: ["payments", "banks"] as const,
  bankAccounts: ["vendor", "bank-accounts"] as const,
  staff: ["vendor", "staff"] as const,
  invites: ["me", "vendor-invites"] as const,
  bulkJobs: ["vendor", "bulk-jobs"] as const,
  bulkJob: (id: string) => ["vendor", "bulk-job", id] as const,
};

// ── Store & onboarding ────────────────────────────────────────────────────────
/** The signed-in user's store, or `null` when they haven't opened one (403 NOT_A_VENDOR). */
export function useMyStore(enabled = true) {
  return useQuery({
    queryKey: vendorKeys.store,
    enabled,
    queryFn: async () => {
      try {
        return await unwrap(api.GET("/api/v1/vendor/store/"));
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useBecomeVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["BecomeVendorRequest"]) => unwrap(api.POST("/api/v1/me/become-vendor/", { body })),
    onSuccess: (store) => {
      qc.setQueryData(vendorKeys.store, store);
      void qc.invalidateQueries({ queryKey: ["vendor"] });
    },
  });
}

export function useUpdateStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["PatchedStoreRequest"]) => unwrap(api.PATCH("/api/v1/vendor/store/", { body })),
    onSuccess: (store) => qc.setQueryData(vendorKeys.store, store),
  });
}

export function usePolicy() {
  return useQuery({ queryKey: vendorKeys.policy, queryFn: () => unwrap(api.GET("/api/v1/vendor/policy/")) });
}

export function useUpdatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["PolicyRequest"]) => unwrap(api.PUT("/api/v1/vendor/policy/", { body })),
    onSuccess: (p) => qc.setQueryData(vendorKeys.policy, p),
  });
}

export function useDashboard() {
  return useQuery({ queryKey: vendorKeys.dashboard, queryFn: () => unwrap(api.GET("/api/v1/vendor/dashboard/")) });
}

// ── Listings ──────────────────────────────────────────────────────────────────
export interface ProductListQuery {
  status?: ProductStatus;
  search?: string;
  page?: number;
  /** Category slug (includes subcategories) */
  category?: string;
  stock?: "out" | "low" | "in";
  boosted?: "true" | "false";
}

/** One card per category the store lists in (live, views, orders, revenue over 30 days). */
export function useCategorySummary() {
  return useQuery({
    queryKey: ["vendor", "category-summary"],
    queryFn: () => unwrap(api.GET("/api/v1/vendor/listings/summary-by-category/")),
    staleTime: 60_000,
  });
}

/** Revenue by category with the best products in each (dashboard overview). */
export function useSalesByCategory(days = 30) {
  return useQuery({
    queryKey: ["vendor", "sales-by-category", days],
    queryFn: () => unwrap(api.GET("/api/v1/vendor/analytics/sales-by-category/", { params: { query: { days } } })),
    staleTime: 5 * 60_000,
  });
}

export function useMyProducts(q: ProductListQuery, enabled = true) {
  return useQuery({
    queryKey: vendorKeys.products(q),
    queryFn: () => unwrap(api.GET("/api/v1/vendor/products/", { params: { query: { ...q, page_size: 20 } } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useMyProduct(id: string | undefined) {
  return useQuery({
    queryKey: vendorKeys.product(id ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/vendor/products/{id}/", { params: { path: { id: id! } } })),
    enabled: !!id,
  });
}

function useProductMutation<V>(fn: (v: V) => Promise<VendorProduct>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (product) => {
      qc.setQueryData(vendorKeys.product(product.id), product);
      void qc.invalidateQueries({ queryKey: ["vendor", "products"] });
      void qc.invalidateQueries({ queryKey: ["vendor", "category-summary"] });
      void qc.invalidateQueries({ queryKey: vendorKeys.dashboard });
      void qc.invalidateQueries({ queryKey: vendorKeys.subscription });
    },
  });
}

export const useCreateProduct = () => useProductMutation((body: ProductWrite) => unwrap(api.POST("/api/v1/vendor/products/", { body })));
export const useUpdateProduct = () =>
  useProductMutation(({ id, body }: { id: string; body: Partial<ProductWrite> }) =>
    unwrap(api.PATCH("/api/v1/vendor/products/{id}/", { params: { path: { id } }, body })),
  );
export const useSetMedia = () =>
  useProductMutation(({ id, media }: { id: string; media: MediaItem[] }) =>
    unwrap(api.PUT("/api/v1/vendor/products/{id}/media/", { params: { path: { id } }, body: { media } })),
  );
export const useSetVariants = () =>
  useProductMutation(({ id, variants }: { id: string; variants: S["VariantRequest"][] }) =>
    unwrap(api.PUT("/api/v1/vendor/products/{id}/variants/", { params: { path: { id } }, body: { variants } })),
  );

export type ProductAction = "publish" | "unpublish" | "renew" | "duplicate" | "mark-sold";

export function useProductAction() {
  return useProductMutation(({ id, action }: { id: string; action: ProductAction }) => {
    const opts = { params: { path: { id } } };
    switch (action) {
      case "publish":
        return unwrap(api.POST("/api/v1/vendor/products/{id}/publish/", opts));
      case "unpublish":
        return unwrap(api.POST("/api/v1/vendor/products/{id}/unpublish/", opts));
      case "renew":
        return unwrap(api.POST("/api/v1/vendor/products/{id}/renew/", opts));
      case "duplicate":
        return unwrap(api.POST("/api/v1/vendor/products/{id}/duplicate/", opts));
      case "mark-sold":
        return unwrap(api.POST("/api/v1/vendor/products/{id}/mark-sold/", opts));
    }
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/api/v1/vendor/products/{id}/", { params: { path: { id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["vendor", "products"] });
      void qc.invalidateQueries({ queryKey: ["vendor", "category-summary"] });
      void qc.invalidateQueries({ queryKey: vendorKeys.dashboard });
    },
  });
}

// ── Bulk upload ───────────────────────────────────────────────────────────────
export function useBulkJobs() {
  return useQuery({ queryKey: vendorKeys.bulkJobs, queryFn: () => unwrap(api.GET("/api/v1/vendor/products/bulk-upload/")) });
}

export function useBulkJob(id: string | undefined) {
  return useQuery({
    queryKey: vendorKeys.bulkJob(id ?? ""),
    queryFn: () => unwrap(api.GET("/api/v1/vendor/products/bulk-upload/{id}/", { params: { path: { id: id! } } })),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && ["PENDING", "PROCESSING"].includes(q.state.data.status) ? 1500 : false),
  });
}

/** Multipart upload (openapi-fetch's JSON body helpers don't cover files). */
export function useStartBulkUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ category, file, publish }: { category: string; file: File; publish: boolean }) => {
      const form = new FormData();
      form.append("category", category);
      form.append("file", file);
      form.append("publish", String(publish));
      const res = await authorizedFetch("/vendor/products/bulk-upload/", { method: "POST", body: form });
      return (await res.json()) as S["BulkUploadJob"];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: vendorKeys.bulkJobs }),
  });
}

/** Fetch with the in-memory access token (for downloads / multipart). Throws ApiError. */
export async function authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  const token = tokenStore.get();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${env.apiBaseUrl}${path}`, { ...init, headers, credentials: "include" });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* not json */
    }
    throw toApiError(res.status, body);
  }
  return res;
}

export async function downloadFile(path: string, filename: string): Promise<void> {
  const res = await authorizedFetch(path);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Subscription ──────────────────────────────────────────────────────────────
export function useSubscription(enabled = true) {
  return useQuery({ queryKey: vendorKeys.subscription, queryFn: () => unwrap(api.GET("/api/v1/vendor/subscription/")), enabled });
}

export function useInvoices() {
  return useQuery({ queryKey: vendorKeys.invoices, queryFn: () => unwrap(api.GET("/api/v1/vendor/subscription/invoices/")) });
}

function useSubMutation<V>(fn: (v: V) => Promise<S["Subscription"]>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (sub) => {
      qc.setQueryData(vendorKeys.subscription, sub);
      void qc.invalidateQueries({ queryKey: vendorKeys.dashboard });
      void qc.invalidateQueries({ queryKey: vendorKeys.invoices });
      void qc.invalidateQueries({ queryKey: ["wallet"] });
    },
  });
}

export const useChangePlan = () =>
  useSubMutation((body: S["ChangePlanRequest"]) => unwrap(api.POST("/api/v1/vendor/subscription/change-plan/", { body })));
export function useSubscribe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }: { plan_code: string; billing_cycle: "MONTHLY" | "ANNUAL"; method: "WALLET" | "CARD"; key: string }) =>
      unwrap(api.POST("/api/v1/vendor/subscription/subscribe/", { params: { header: { "Idempotency-Key": key } }, body })),
    onSuccess: (res) => {
      qc.setQueryData(vendorKeys.subscription, res.subscription);
      void qc.invalidateQueries({ queryKey: vendorKeys.invoices });
      void qc.invalidateQueries({ queryKey: vendorKeys.dashboard });
      void qc.invalidateQueries({ queryKey: ["wallet"] });
    },
  });
}

export const useCancelSubscription = () => useSubMutation(() => unwrap(api.POST("/api/v1/vendor/subscription/cancel/")));
export const useResumeSubscription = () => useSubMutation(() => unwrap(api.POST("/api/v1/vendor/subscription/resume/")));

// ── KYC & bank accounts ───────────────────────────────────────────────────────
export function useKyc() {
  return useQuery({ queryKey: vendorKeys.kyc, queryFn: () => unwrap(api.GET("/api/v1/vendor/kyc/")) });
}

export function useSubmitIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["IdentitySubmitRequest"]) => unwrap(api.POST("/api/v1/vendor/kyc/identity/", { body })),
    onSuccess: (data) => {
      qc.setQueryData(vendorKeys.kyc, data);
      void qc.invalidateQueries({ queryKey: vendorKeys.store });
      void qc.invalidateQueries({ queryKey: vendorKeys.bankAccounts });
    },
  });
}

export function useSubmitBusinessDocs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["BusinessDocsRequest"]) => unwrap(api.POST("/api/v1/vendor/kyc/business/", { body })),
    onSuccess: (data) => {
      qc.setQueryData(vendorKeys.kyc, data);
      void qc.invalidateQueries({ queryKey: vendorKeys.store });
    },
  });
}

export function useBanks(enabled = true) {
  return useQuery({
    queryKey: vendorKeys.banks,
    queryFn: () => unwrap(api.GET("/api/v1/payments/banks/")),
    enabled,
    staleTime: 24 * 60 * 60_000,
    retry: false,
  });
}

export function useBankAccounts() {
  return useQuery({ queryKey: vendorKeys.bankAccounts, queryFn: () => unwrap(api.GET("/api/v1/vendor/bank-accounts/")) });
}

export function useResolveAccount() {
  return useMutation({
    mutationFn: (body: S["BankResolveRequest"]) => unwrap(api.POST("/api/v1/vendor/bank-accounts/resolve/", { body })),
  });
}

export function useAddBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["BankAccountCreateRequest"]) => unwrap(api.POST("/api/v1/vendor/bank-accounts/", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vendorKeys.bankAccounts });
      void qc.invalidateQueries({ queryKey: vendorKeys.kyc });
      void qc.invalidateQueries({ queryKey: vendorKeys.store });
    },
  });
}

export function useBankAccountAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: "default" | "delete" }) =>
      action === "default"
        ? unwrap(api.POST("/api/v1/vendor/bank-accounts/{id}/default/", { params: { path: { id } } }))
        : unwrap(api.DELETE("/api/v1/vendor/bank-accounts/{id}/", { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: vendorKeys.bankAccounts }),
  });
}

// ── Staff ─────────────────────────────────────────────────────────────────────
export function useStaff() {
  return useQuery({ queryKey: vendorKeys.staff, queryFn: () => unwrap(api.GET("/api/v1/vendor/staff/")) });
}

export function useInviteStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: S["StaffInviteRequest"]) => unwrap(api.POST("/api/v1/vendor/staff/", { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: vendorKeys.staff }),
  });
}

export function useStaffAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role?: S["StaffRoleEnum"] }) =>
      role
        ? unwrap(api.PATCH("/api/v1/vendor/staff/{id}/", { params: { path: { id } }, body: { role } }))
        : unwrap(api.DELETE("/api/v1/vendor/staff/{id}/", { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: vendorKeys.staff }),
  });
}

export function useMyInvites(enabled = true) {
  return useQuery({ queryKey: vendorKeys.invites, queryFn: () => unwrap(api.GET("/api/v1/me/vendor-invites/")), enabled });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/api/v1/me/vendor-invites/{id}/accept/", { params: { path: { id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: vendorKeys.invites });
      void qc.invalidateQueries({ queryKey: vendorKeys.store });
    },
  });
}
